import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import {
  AdminPageHeader,
  Button,
  ButtonSize,
  ButtonStyles,
  DropDown,
  Input,
  // Checkbox,
  // FAIcon,
  // InputTypes,
  InputValidations,
  Label,
  MLPVersion,
  PhoneNumber,
  Position,
  Search,
  TextArea,
  Theme,
} from '../../../../../ui/components';
import { LoggerService, PAGES, isEmpty, trackPage } from '../../../../../ui/utils';
import { DynamicObjectInterface } from '../../../../../sdk/type';
import ParticipantService from '../../../../../sdk/services/participantService';
import { useAPICall } from '../../../../../hooks';
import MDMSService from '../../../../../sdk/services/mdmsService';
import { initTeamForm } from '../[teamId]';
import { SearchedUserData, TeamData, AddParticipantStructure } from '../../teamsListConfig';
import TeamService from '../../../../../sdk/services/teamService';
import CampaignService from '../../../../../sdk/services/campaignService';
import { useAdminToast } from '../../../../../ui/components/AdminToast/AdminToast';
import { countryCodeNumber } from '../../../../../__mockData/phoneNumber';
import { DropDownData } from '../../../../../ui/components/Form/DropDown/types/propsType';
import { formatPhoneNumberForMDMS, isValidEmail } from '../../../../../ui/utils/common';
import ValidationService from '../../../../../sdk/services/validationService';
import styles from './index.module.scss';

export interface User {
  id: string;
  title: string;
}

export interface OrderData {
  success: boolean;
  errorMessage?: string;
  orderNumber?: string;
  orderName?: string;
  orderAmount?: string;
}

interface ActivityValueAmount {
  amount: number;
  currency: string;
}

interface ActivityRule {
  type: string;
  operator: string;
  value: ActivityValueAmount | string[][] | { base: string[][]; addOn: string[][] };
  combine: string;
}

interface Activities {
  maxOrderNumber: number;
  orderPeriod: number;
  rules: ActivityRule[];
  combineRules: string;
}

export interface CampaignConfigDataOrder {
  recId: number;
  campaignId: string;
  campaignName: string;
  campaignDescription: string;
  campaignStart: string;
  campaignEnd: string;
  campaignStatus: string;
  campaignTextLink: string;
  campaignTextLinkDisplay: number;
  activities: Activities;
  configJson: DynamicObjectInterface;
}

export const AddParticipant = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('');

  const [teamData, setTeamData] = useState<TeamData>(initTeamForm);
  const [userData, setUserData] = useState<SearchedUserData[]>([]);
  const [selectedUserData, setSelectedUserData] = useState<SearchedUserData | null>(null);
  const [dropdownValue, setDropdownValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  // const [invisible, setInvisible] = useState(false);
  // const [emailValid, setEmailValid] = useState(true);
  const [emailValidUser, setEmailValidUser] = useState(true);
  const [phoneLength, setPhoneLength] = useState(false);
  const [displayCard, setDisplayCard] = useState(false);
  const [participantInvalid, setParticipantInvalid] = useState(true);
  const [orderNumberValid, setOrderNumberValid] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [invalidOrderMessage, setInvalidOrderMessage] = useState<string>('');
  const [campaignData, setCampaignData] = useState<CampaignConfigDataOrder>();
  const [addParticipantData, setAddParticipantData] = useState<AddParticipantStructure>({
    campaignId: '',
    loginId: '',
    coachId: '',
    enrollmentDate: '',
    directLeaderName: '',
    pinLevel: '',
    directLeaderId: 0,
    orderNumber: '',
    directLeaderPinLevel: 0,
    endDate: '',
    remarks: '',
    invisibleParticipant: {
      name: '',
      email: '',
      phone: '',
    },
  });

  const [orderData, setOrderData] = useState<OrderData>();

  useEffect(() => {
    fetchTeamData();

    const timeout = setTimeout(() => {
      trackPage(PAGES.addParticipant);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (userData?.length === 1) {
      setSelectedUserData(userData[0]);
    }
  }, [userData]);

  useEffect(() => {
    if (selectedUserData) {
      const { phone } = selectedUserData;
      setDisplayCard(true);
      validateParticipant();
      if (phone) {
        const [countryCode, number] = phone.split(' ');
        setDropdownValue(countryCode);
        setPhoneValue(number);
      }
    }
  }, [selectedUserData]);

  useEffect(() => {
    validateParticipant();
  }, [addParticipantData]);

  const fetchTeamData = async () => {
    try {
      const teamResult = await apiCall(TeamService.getTeamDetails, `${teamId}`);
      const teamData = teamResult?.data?.data ?? initTeamForm;
      setTeamData(teamData);

      if (teamData.campaignId) {
        const campaignResult = await apiCall(CampaignService.getCampaignDetailByCampaignId, `${teamData.campaignId}`);
        const campaignData = campaignResult?.data?.data ?? {};
        setCampaignData(campaignData);
      }
    } catch (error) {
      LoggerService.log('Failed to retrieve team or campaign detail', error);
    }
  };

  const validateParticipant = () => {
    const { loginId, orderNumber } = addParticipantData ?? {};
    const isAnyFieldEmpty = !orderNumber;
    const { name } = selectedUserData ?? {};
    setParticipantInvalid(!loginId || !name || isAnyFieldEmpty || !emailValidUser || phoneLength);
  };

  const handleSelectedUserChange = (name: string, value: string) => {
    setSelectedUserData(prevUserData => ({
      ...prevUserData!,
      [name]: value,
    }));
    if (name === 'email') {
      setEmailValidUser(isValidEmail(value));
    }
  };

  const onSave = async () => {
    let defaultOrder = {
      orderStatus: orderData?.success ? 1 : 0,
      errorMessage: orderData?.errorMessage ?? '',
      orderNumber: orderData?.orderNumber ?? '',
      orderName: orderData?.orderName ?? '',
      orderAmount: orderData?.orderAmount ?? null,
    };

    let combinedRemark = `${addParticipantData?.remarks} ${defaultOrder.errorMessage}`;

    const payload = {
      campaignId: teamData.campaignId,
      campaignName: teamData.campaignName,
      teamId: teamData.teamId,
      teamName: teamData.isIndividualTeam !== 1 ? teamData.teamName : selectedUserData?.name ,
      isIndividualTeam: teamData.isIndividualTeam,
      name: selectedUserData?.name,
      email: selectedUserData?.email,
      phone: selectedUserData?.phone,
      loginId: addParticipantData.loginId,
      coachId: teamData.coachId,
      remarks: combinedRemark,
      pinLevel: selectedUserData?.pinLevel,
      orderNumber: addParticipantData?.orderNumber,
      orderAmount: defaultOrder.orderAmount,
      orderStatus: defaultOrder.orderStatus, // validation fail give 0/1
      orderName: defaultOrder.orderName,
      directLeaderId: selectedUserData?.directLeaderId,
      directLeaderName: selectedUserData?.directLeaderName,
      directLeaderPinLevel: selectedUserData?.directLeaderPinLevel,
      invisibleParticipant: {
        name: addParticipantData.invisibleParticipant.name,
        email: addParticipantData.invisibleParticipant.email,
        phone: addParticipantData.invisibleParticipant.phone,
      },
      enrollmentDate: new Date().toISOString().slice(0, -1),
      endDate: new Date().toISOString().slice(0, -1),
      isNewAbo: selectedUserData?.isNewAbo,
      campaignConsent: false,
      gender: selectedUserData?.gender,
      race: selectedUserData?.race,
      address: selectedUserData?.address,
      loaName: selectedUserData?.loaName,
      accountType: selectedUserData?.accountType,
      highestPinLevel: selectedUserData?.highestPinLevel,
      partyId: selectedUserData?.partyId,
    };
    const result = await apiCall(ParticipantService.addParticipant, payload);

    if (!result?.data?.result) {
      if (result.status !== 500) {
        adminToast({
          message: result?.data?.message,
          theme: Theme.ERROR,
        });
      }
    } else {
      navigate(`/admin/manage-team/edit?=${teamId}`);
      adminToast({
        message: t('admin.addParticipant.addedParticipantSuccess'),
        theme: Theme.SUCCESS,
      });
    }
  };

  const searchUser = async () => {
    let registeredAcc: SearchedUserData[] = [];
    let underAboAcc: SearchedUserData[] = [];
    let aboNumber = addParticipantData.loginId;
    if (aboNumber.length > 3) {
      const [resRegisteredAcc, resAccUnderAbo] = await Promise.all([
        apiCall(ParticipantService.searchParticipant, aboNumber),
        apiCall(MDMSService.getMDMSUserData, { aboNumber: aboNumber, pinCriteria: null }),
      ]);
      const resultRegisteredAcc: SearchedUserData[] = resRegisteredAcc.data?.data;
      const resultUnderAboAcc = resAccUnderAbo.data?.data;

      LoggerService.log('resultRegisteredAcc >>>', resultRegisteredAcc);
      LoggerService.log('resultUnderAboAcc >>>', resultUnderAboAcc);
      if (!isEmpty(resultUnderAboAcc)) {
        underAboAcc.push({
          name: resultUnderAboAcc?.primaryPartyDtl?.fullName,
          phone: formatPhoneNumberForMDMS(resultUnderAboAcc?.primaryPartyDtl?.phoneNumber),
          email: resultUnderAboAcc?.primaryPartyDtl?.email,
          pinLevel: resultUnderAboAcc?.pin,
          directLeaderId: resultUnderAboAcc?.sponsor?.sponsorABONo,
          directLeaderName: resultUnderAboAcc?.sponsor?.sponsorName,
          directLeaderPinLevel: resultUnderAboAcc?.sponsor?.pin,
          isNewAbo: resultUnderAboAcc?.isNewAbo,
          gender: resultUnderAboAcc?.primaryPartyDtl?.gender?.toUpperCase(),
          race: resultUnderAboAcc?.primaryPartyDtl?.race?.toUpperCase(),
          address: resultUnderAboAcc?.primaryPartyDtl?.address,
          loaName: resultUnderAboAcc?.loaName,
          accountType: resultUnderAboAcc?.accountType,
          highestPinLevel: resultUnderAboAcc?.highestPinLevel,
          partyId: resultUnderAboAcc?.primaryPartyDtl?.partyId,
        });

        if (resultUnderAboAcc?.isCouple) {
          underAboAcc.push({
            name: resultUnderAboAcc?.secondaryPartyDtl?.fullName,
            phone: formatPhoneNumberForMDMS(resultUnderAboAcc?.secondaryPartyDtl?.phoneNumber),
            email: resultUnderAboAcc?.secondaryPartyDtl?.email,
            pinLevel: resultUnderAboAcc?.pin,
            directLeaderId: resultUnderAboAcc?.sponsor?.sponsorABONo,
            directLeaderName: resultUnderAboAcc?.sponsor?.sponsorName,
            directLeaderPinLevel: resultUnderAboAcc?.sponsor?.pin,
            isNewAbo: resultUnderAboAcc?.isNewAbo,
            gender: resultUnderAboAcc?.primaryPartyDtl?.gender?.toUpperCase(),
            race: resultUnderAboAcc?.primaryPartyDtl?.race?.toUpperCase(),
            address: resultUnderAboAcc?.primaryPartyDtl?.address,
            loaName: resultUnderAboAcc?.loaName,
            accountType: resultUnderAboAcc?.accountType,
            highestPinLevel: resultUnderAboAcc?.highestPinLevel,
            partyId: resultUnderAboAcc?.secondaryPartyDtl?.partyId,
          });
        }
      }

      if (resultRegisteredAcc?.length > 0) {
        registeredAcc = resultRegisteredAcc.map(reg => ({
          name: reg.name,
          phone: reg.phone,
          email: reg.email,
          pinLevel: reg.pinLevel,
          directLeaderId: reg.directLeaderId,
          directLeaderName: reg.directLeaderName,
          directLeaderPinLevel: reg.directLeaderPinLevel,
          isNewAbo: underAboAcc.find(acc => acc.name === reg.name)?.isNewAbo ?? false,
          gender: reg.gender,
          race: reg.race,
          address: reg.address,
          loaName: reg.loaName,
          accountType: reg.accountType,
          highestPinLevel: reg.highestPinLevel,
          partyId: reg.partyId,
        }));
      }

      let accounts = registeredAcc.concat(underAboAcc);
      let _userData = [...new Set(accounts.map(acc => acc.name))].map(acc => accounts.find(a => a.name === acc));

      setUserData(_userData as SearchedUserData[]);
      setSelectedUserData(_userData?.[0] ?? null);
    }
  };

  const handleOnClearAboId = () => {
    setAddParticipantData(prevValue => {
      prevValue.invisibleParticipant.name = '';
      prevValue.invisibleParticipant.email = '';
      prevValue.invisibleParticipant.phone = '';
      prevValue.loginId = '';
      return { ...prevValue };
    });
  };

  const handleInputChange = (name: string, value: string) => {
    if (name === 'orderNumber') {
      const regex = /^[0-9,]*$/;

      if (!regex.test(value)) {
        return;
      }
    }
    // Split the input by commas and trim each part
    const orders = value.split(',').map(order => order.trim());

    // Limit order numbers
    if (orders.length > (campaignData?.activities?.maxOrderNumber ?? 3)) {
      orders.splice(campaignData?.activities?.maxOrderNumber ?? 3);
      value = orders.join(',');
    }

    const keys = name.split('.');
    setAddParticipantData((prevData: DynamicObjectInterface) => {
      let newData = { ...prevData };
      let currentLevel = newData;
      for (let i = 0; i < keys?.length - 1; i++) {
        if (!currentLevel[keys[i]]) {
          currentLevel[keys[i]] = {};
        }
        currentLevel = currentLevel[keys[i]];
      }
      currentLevel[keys[keys?.length - 1]] = value;

      return newData;
    });
    // if (name === 'invisibleParticipant.email') {
    //   setEmailValid(isValidEmail(value));
    // }
  };

  const handleVerifyOrder = async () => {
    setVerificationAttempted(true);

    const payload = {
      campaignId: teamData?.campaignId,
      aboId: addParticipantData?.loginId,
      salesOrderNumber: addParticipantData?.orderNumber,
    };

    if (addParticipantData.orderNumber !== '') {
      try {
        const response = await apiCall(ValidationService.validateOrderNumber, payload);
        const result: OrderData = response.data[0];
        setOrderData(result);
        if (result.success) {
          setOrderNumberValid(true);
          setInvalidOrderMessage('The order number(s) provided are valid');
        } else {
          setOrderNumberValid(false);
          setInvalidOrderMessage(
            'The order number(s) provided above will need to be verified by Amway. We will reach out if additional information is needed.',
          );
        }
      } catch (error) {
        LoggerService.log('Error verifying order:', error);
        setOrderNumberValid(false);
        setInvalidOrderMessage('An error occurred while verifying the order numbers. Please try again later.');
      }
    } else {
      setOrderNumberValid(false);
    }
  };

  const buttonConfig = [
    {
      title: t('admin.addParticipant.register'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: participantInvalid,
      onClick: onSave,
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={t('admin.addParticipant.title')}
        button={buttonConfig}
        withBackButton
        onBackButtonClick={() => {
          navigate(`/admin/manage-team/edit?=${teamId}`);
        }}
      />
      <div className={styles['add-participant-form-container']}>
        <div className={styles['input-container']}>
          <div className={styles['pin-dropdown']}>
            <Input
              name={'campaignName'}
              placeholder={t('admin.addParticipant.campaignTitle')}
              value={teamData.campaignName}
              disabled
              mlpVersion={MLPVersion.ONE}
              label={t('admin.addParticipant.campaignTitle')}
            />
          </div>
          <div>
            <Input
              name={'teamName'}
              placeholder={t('admin.addParticipant.team')}
              value={teamData.teamName}
              disabled
              mlpVersion={MLPVersion.ONE}
              label={t('admin.addParticipant.team')}
            />
          </div>
        </div>
        <div className={styles['input-container']}>
          <TextArea
            mlpVersion={MLPVersion.ONE}
            label={t('admin.addParticipant.description')}
            placeholder={t('admin.addParticipant.description')}
            value={teamData?.description}
            disabled
            onChange={e => {
              handleInputChange('remarks', e.target.value);
            }}
          />
        </div>
      </div>
      <hr className={styles['horizontal-line']} />

      <div className={styles['add-participant-form-container']}>
        <div>
          <Search
            label={`${t('admin.addParticipant.aboId')}*`}
            message="Click enter to start searching this ABO ID"
            value={addParticipantData.loginId}
            onChange={e => handleInputChange('loginId', e.target.value)}
            onSearch={() => searchUser()}
            onClear={() => {
              handleOnClearAboId();
              //setInvisible(false);
              setUserData([]);
              setSelectedUserData(null);
              setParticipantInvalid(true);
              setDisplayCard(false);
            }}
          />
        </div>
        <div>
          {userData.length > 1 ? (
            <DropDown
              name="name"
              dropdownLabel={`${t('admin.addParticipant.participantName')}*`}
              placeholder={t('admin.addParticipant.participantName')}
              disabled={userData?.length === 1}
              mlpVersion={MLPVersion.ONE}
              value={selectedUserData?.name}
              data={userData?.map(user => ({ id: user.name, title: user.name }))}
              configData={{
                label: 'title',
                onSelect: (selectedOption: DropDownData) => {
                  setSelectedUserData(userData.find(user => user.name === selectedOption.title) ?? null);
                },
              }}
            />
          ) : (
            <Input
              name="name"
              label={`${t('admin.addParticipant.participantName')}*`}
              value={selectedUserData?.name || ''}
              mlpVersion={MLPVersion.ONE}
              disabled
            />
          )}
        </div>
        <div>
          <Input
            name="email"
            label={`${t('admin.userSetup.email')}*`}
            value={selectedUserData?.email}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleSelectedUserChange('email', e.target.value);
            }}
            status={!emailValidUser ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={emailValidUser ? '' : `Please enter a valid email address`}
          />
        </div>
        <div>
          <div className={styles['label-text-phone']}>{t('admin.userSetup.phone')}*</div>
          <PhoneNumber
            name={'phoneNumber'}
            data={countryCodeNumber}
            inputValue={phoneValue}
            dropdownValue={dropdownValue}
            placeholder="Phone Number"
            status={phoneLength ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={phoneLength ? t('admin.userSetup.requiredPhone') : ''}
            configData={{
              label: 'name',
              onSelect: (e: DropDownData) => {
                setDropdownValue(e.name as string);
                // Check if selectedOption is not empty before setting phoneValue
                if (e.name) {
                  const combinedValue = `${e.name} ${phoneValue}`;
                  setPhoneValue(phoneValue === '' ? '' : phoneValue); // Ensure phoneValue is not set if it's already empty
                  handleSelectedUserChange('phone', combinedValue);
                } else {
                  setPhoneValue(''); // Reset phoneValue to empty if dropdownValue is not selected
                  handleSelectedUserChange('phone', ''); // Update the state accordingly
                }
              },
            }}
            onInputChange={val => {
              const phoneNumber = val ?? '';
              setPhoneValue(phoneNumber);
              setPhoneLength(phoneNumber.length > 0 && (phoneNumber.length < 8 || phoneNumber.length > 10));
              if (phoneNumber) {
                const combinedValue = `${dropdownValue} ${phoneNumber}`;
                setPhoneValue(phoneNumber === '' ? '' : phoneNumber);
                handleSelectedUserChange('phone', combinedValue);
              } else {
                setPhoneValue(''); // Reset phoneValue to empty if dropdownValue is not selected
                handleSelectedUserChange('phone', ''); // Update the state accordingly
              }
            }}
          />
        </div>
        <div className={styles['participant-details-order-number-container']}>
          <Input
            name="orderNumber"
            label={`${t('Order Number')}`}
            value={addParticipantData?.orderNumber}
            required
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('orderNumber', e.target.value.toString());
              if (verificationAttempted) setVerificationAttempted(false);
            }}
            message={
              addParticipantData?.orderNumber !== ''
                ? verificationAttempted
                  ? orderNumberValid
                    ? `Valid Order Number(s)`
                    : invalidOrderMessage
                  : `*Enter up to ${campaignData?.activities.maxOrderNumber ?? 3} order numbers, in full and separated by commas (e.g. "50004212345, 50004267890, 50004211223").`
                : `*Enter up to ${campaignData?.activities.maxOrderNumber ?? 3} order numbers, in full and separated by commas (e.g. "50004212345, 50004267890, 50004211223").`
            }
            status={
              addParticipantData?.orderNumber !== ''
                ? verificationAttempted
                  ? orderNumberValid
                    ? InputValidations.VALID
                    : InputValidations.WARNING
                  : InputValidations.DEFAULT
                : InputValidations.DEFAULT
            }
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleVerifyOrder();
              }
            }}
          />
        </div>
        <Button
          className={styles['verify-order']}
          size={ButtonSize.SMALL}
          disabled={addParticipantData?.orderNumber === ''}
          title="Verify Order"
          onClick={handleVerifyOrder}
        />
      </div>
      {displayCard ? (
        <div className={styles['participant-details-container']}>
          <div className={styles['participant-details-title']}>{t('admin.addParticipant.participantDetailsTitle')}</div>
          <div className={styles['add-participant-form-container-three-column']}>
            <Label
              title={t('admin.addParticipant.pinLevel')}
              position={Position.HORIZONTAL}
              value={selectedUserData?.pinLevel?.toString() ?? '-'}
            />
            <Label
              title={t('admin.addParticipant.uplineID')}
              position={Position.HORIZONTAL}
              value={selectedUserData?.directLeaderId?.toString() ?? '-'}
            />
            <Label
              title={t('admin.addParticipant.uplineName')}
              position={Position.HORIZONTAL}
              value={selectedUserData?.directLeaderName ?? '-'}
            />
            <Label
              title={t('admin.addParticipant.uplinePin')}
              position={Position.HORIZONTAL}
              value={selectedUserData?.directLeaderPinLevel?.toString() ?? '-'}
            />
          </div>
        </div>
      ) : null}
      {/* {userData.length === 1 && (
        <div className={styles['individual-invisible-container']}>
          <Checkbox
            text=""
            checked={invisible}
            onChange={() => {
              setInvisible(!invisible);
            }}
          />
          <div className={styles['individual-invisible-title']}>
            <div className={styles['individual-invisible-label']}>{t('admin.addParticipant.individual')}</div>
            <div className={styles['individual-invisible-icon']}>
              <FAIcon iconName={'eye-slash'} />
            </div>
          </div>
        </div>
      )} */}
      {/* {invisible && (
        <div className={styles['individual-form-container']}>
          <div className={styles['add-participant-form-container-three-column']}>
            <div className={styles['input-container']}>
              <Input
                mlpVersion={MLPVersion.ONE}
                type={InputTypes.TEXT}
                name={'invisibleName'}
                label={`${t('admin.addParticipant.name')}*`}
                placeholder={t('admin.addParticipant.name')}
                value={
                  addParticipantData?.invisibleParticipant.name !== undefined
                    ? addParticipantData.invisibleParticipant.name
                    : ''
                }
                onChange={e => {
                  handleInputChange('invisibleParticipant.name', e.target.value);
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.EMAIL}
                name={'email'}
                placeholder="john_doe@domainname.com"
                label={`${t('admin.userSetup.email')}*`}
                value={
                  addParticipantData?.invisibleParticipant.email !== undefined
                    ? addParticipantData?.invisibleParticipant.email
                    : ''
                }
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChange('invisibleParticipant.email', e.target.value);
                }}
                status={!emailValid ? InputValidations.INVALID : InputValidations.DEFAULT}
                message={emailValid ? '' : t('admin.userSetup.invalidEmailFormat')}
              />
            </div>
            <div className={styles['input-container']}>
              <div className={styles['label-text-phone']}>{`${t('admin.userSetup.phone')}*`}</div>
              <PhoneNumber
                name={'phoneNumber'}
                data={countryCodeNumber}
                inputValue={phoneValue}
                dropdownValue={dropdownValue}
                placeholder={`${t('admin.addParticipant.phone')}`}
                configData={{
                  label: 'name',
                  onSelect: (e: DropDownData) => {
                    setDropdownValue(e.name as string);
                    // Check if selectedOption is not empty before setting phoneValue
                    if (e.name) {
                      const combinedValue = `${e.name} ${phoneValue}`;
                      setPhoneValue(phoneValue === '' ? '' : phoneValue); // Ensure phoneValue is not set if it's already empty
                      handleInputChange('invisibleParticipant.phone', combinedValue);
                    } else {
                      setPhoneValue(''); // Reset phoneValue to empty if dropdownValue is not selected
                      handleInputChange('invisibleParticipant.phone', ''); // Update the state accordingly
                    }
                  },
                }}
                onInputChange={val => {
                  const phoneNumber = val ?? '';
                  setPhoneValue(phoneNumber);
                  if (phoneNumber) {
                    const combinedValue = `${dropdownValue} ${phoneNumber}`;
                    setPhoneValue(phoneNumber === '' ? '' : phoneNumber);
                    handleInputChange('invisibleParticipant.phone', combinedValue);
                  } else {
                    setPhoneValue(''); // Reset phoneValue to empty if dropdownValue is not selected
                    handleInputChange('invisibleParticipant.phone', ''); // Update the state accordingly
                  }
                }}
              />
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};
